// frontend/src/components/admin/SwitchConsumption.jsx

import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import api from '../../services/api';
import Card from '../common/Card';
import Button from '../common/Button';
import Loading from '../common/Loading';

// Enregistrer les composants Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const SwitchConsumption = ({ switchId, deviceData }) => {
  const [consumption, setConsumption] = useState(null);
  const [history, setHistory] = useState([]);
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(true);
  const [capturing, setCapturing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // ✅ VÉRIFIER QUE switchId existe
    if (switchId) {
      console.log('📊 SwitchConsumption monté avec switchId:', switchId);
      fetchData();
    } else {
      console.error('❌ switchId est undefined');
      setLoading(false);
      setError('ID du device non disponible');
    }
  }, [switchId, period]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('📊 Récupération données pour switchId:', switchId);
      
      const [consumptionRes, historyRes] = await Promise.all([
        api.get(`/meter-indexes/${switchId}/consumption`),
        api.get(`/meter-indexes/${switchId}/history`, { params: { period } }),
      ]);

      console.log('✅ Consommation reçue:', consumptionRes.data);
      console.log('✅ Historique reçu:', historyRes.data);

      setConsumption(consumptionRes.data.data);
      setHistory(historyRes.data.data);
    } catch (error) {
      console.error('❌ Erreur récupération consommation:', error);
      
      // Si pas d'index disponible, afficher un message
      if (error.response?.status === 500 || error.response?.status === 404) {
        setConsumption(null);
        setHistory([]);
        setError('Aucune donnée de consommation disponible. Capturez d\'abord un index.');
      } else {
        setError('Erreur lors de la récupération des données');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCaptureIndex = async () => {
    if (!switchId) {
      alert('ID du device non disponible');
      return;
    }

    try {
      setCapturing(true);
      console.log('📸 Capture index pour switchId:', switchId);
      await api.post(`/meter-indexes/${switchId}/capture`);
      alert('Index capturé avec succès !');
      fetchData();
    } catch (error) {
      console.error('❌ Erreur capture:', error);
      alert('Erreur lors de la capture: ' + (error.response?.data?.message || error.message));
    } finally {
      setCapturing(false);
    }
  };

  if (loading) return <Loading />;

  // Si erreur ou pas de switchId
  if (error || !switchId) {
    return (
      <Card title="📊 Consommation & Facturation">
        <div className="text-center py-8">
          <p className="text-red-600 mb-4">
            {error || 'ID du device non disponible'}
          </p>
          {switchId && (
            <Button onClick={handleCaptureIndex} disabled={capturing}>
              {capturing ? 'Capture en cours...' : '📸 Capturer l\'index'}
            </Button>
          )}
        </div>
      </Card>
    );
  }

// frontend/src/components/admin/SwitchConsumption.jsx

// Si pas de données disponibles
if (!consumption) {
    return (
      <Card title="📊 Consommation & Facturation">
        <div className="text-center py-8">
          <div className="mb-6">
            <div className="text-6xl mb-4">📊</div>
            <p className="text-gray-800 font-semibold text-lg mb-2">
              Aucune donnée de consommation disponible
            </p>
            <p className="text-gray-600 mb-2">
              Ce device n'a pas encore d'index enregistré.
            </p>
            <p className="text-sm text-gray-500">
              Cliquez sur le bouton ci-dessous pour capturer le premier index et commencer le suivi de consommation.
            </p>
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 max-w-md mx-auto">
            <p className="text-sm text-blue-800">
              <strong>💡 Note :</strong> L'index sera capturé automatiquement tous les jours aux horaires configurés (10h, 15h, 21h) et le 1er de chaque mois à 00:01.
            </p>
          </div>
  
          <Button onClick={handleCaptureIndex} disabled={capturing} size="lg">
            {capturing ? (
              <>
                <span className="animate-spin mr-2">⏳</span>
                Capture en cours...
              </>
            ) : (
              <>
                <span className="mr-2">📸</span>
                Capturer le premier index
              </>
            )}
          </Button>
        </div>
      </Card>
    );
  }

  // Données du graphique
  const chartData = {
    labels: history.map((h) => h.date),
    datasets: [
      {
        label: 'Consommation quotidienne (kWh)',
        data: history.map((h) => h.consumed),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.4,
      },
      {
        label: 'Coût HT (FCFA)',
        data: history.map((h) => h.costHT),
        borderColor: 'rgb(16, 185, 129)',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        tension: 0.4,
        yAxisID: 'y1',
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    scales: {
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: 'kWh',
        },
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        title: {
          display: true,
          text: 'FCFA',
        },
        grid: {
          drawOnChartArea: false,
        },
      },
    },
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            label += context.parsed.y.toFixed(2);
            if (context.dataset.label.includes('FCFA')) {
              label += ' FCFA';
            } else {
              label += ' kWh';
            }
            return label;
          },
        },
      },
    },
  };

  return (
    <div className="space-y-6">
      {/* Header avec période et capture */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">
          📊 Consommation & Facturation
        </h2>
        <div className="flex space-x-3">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-primary"
          >
            <option value="week">7 derniers jours</option>
            <option value="month">Ce mois</option>
            <option value="year">Cette année</option>
          </select>
          <Button onClick={handleCaptureIndex} disabled={capturing} variant="secondary">
            {capturing ? '⏳' : '📸'} Capturer l'index
          </Button>
        </div>
      </div>

      {/* Cartes de statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
          <h3 className="text-sm font-semibold mb-2">Index Actuel</h3>
          <p className="text-3xl font-bold">{consumption.indexes.end.toFixed(2)}</p>
          <p className="text-xs mt-1">kWh</p>
        </Card>

        <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white">
          <h3 className="text-sm font-semibold mb-2">Consommé ce mois</h3>
          <p className="text-3xl font-bold">{consumption.indexes.consumed.toFixed(2)}</p>
          <p className="text-xs mt-1">kWh</p>
        </Card>

        <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white">
          <h3 className="text-sm font-semibold mb-2">Montant HT</h3>
          <p className="text-3xl font-bold">{consumption.costs.montantHT.toLocaleString()}</p>
          <p className="text-xs mt-1">FCFA</p>
        </Card>

        <Card className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
          <h3 className="text-sm font-semibold mb-2">Total TTC</h3>
          <p className="text-3xl font-bold">{consumption.costs.totalTTC.toLocaleString()}</p>
          <p className="text-xs mt-1">FCFA</p>
        </Card>
      </div>

      {/* Détails de la facture */}
      <Card title="💰 Détail de la facture (Mois en cours)">
        <div className="space-y-4">
          {/* Période */}
          <div className="pb-3 border-b border-gray-200">
            <p className="text-sm text-gray-600">
              Période: {new Date(consumption.period.start).toLocaleDateString('fr-FR')} -{' '}
              {new Date(consumption.period.end).toLocaleDateString('fr-FR')}
            </p>
          </div>

          {/* Index */}
          <div className="grid grid-cols-3 gap-4 pb-3 border-b border-gray-200">
            <div>
              <p className="text-sm text-gray-600">Index début</p>
              <p className="text-lg font-bold text-gray-800">
                {consumption.indexes.start.toFixed(2)} kWh
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Index actuel</p>
              <p className="text-lg font-bold text-gray-800">
                {consumption.indexes.end.toFixed(2)} kWh
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Consommé</p>
              <p className="text-lg font-bold text-blue-600">
                {consumption.indexes.consumed.toFixed(2)} kWh
              </p>
            </div>
          </div>

          {/* Consommation */}
          <div className="space-y-2">
            <h4 className="font-semibold text-gray-800">Consommation</h4>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">
                {consumption.indexes.consumed.toFixed(2)} kWh × {consumption.config.pricePerKwh} FCFA
              </span>
              <span className="font-bold text-gray-800">
                {consumption.costs.montantHT.toLocaleString()} FCFA
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">
                TVA consommation ({(consumption.config.tvaRate * 100).toFixed(0)}%)
              </span>
              <span className="font-bold text-gray-800">
                {consumption.costs.tvaConsommation.toLocaleString()} FCFA
              </span>
            </div>
          </div>

          {/* Prime fixe */}
          <div className="space-y-2 pt-3 border-t border-gray-200">
            <h4 className="font-semibold text-gray-800">Prime fixe</h4>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">
                Prime fixe ({consumption.config.reglageDisjoncteur})
              </span>
              <span className="font-bold text-gray-800">
                {consumption.costs.primeFixe.toLocaleString()} FCFA
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">TVA prime fixe (18%)</span>
              <span className="font-bold text-gray-800">
                {consumption.costs.tvaPrimeFixe.toLocaleString()} FCFA
              </span>
            </div>
          </div>

          {/* Redevances */}
          <div className="space-y-2 pt-3 border-t border-gray-200">
            <h4 className="font-semibold text-gray-800">Redevances et taxes</h4>
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600">Électrification rurale</span>
              <span className="text-gray-800">
                {consumption.details.redevanceElectrificationRurale.toLocaleString()} FCFA
              </span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600">Ordures ménagères</span>
              <span className="text-gray-800">
                {consumption.details.taxeOrduresMenageres.toLocaleString()} FCFA
              </span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600">RTI</span>
              <span className="text-gray-800">
                {consumption.details.redevanceRTI.toLocaleString()} FCFA
              </span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600">Timbre d'État</span>
              <span className="text-gray-800">
                {consumption.details.timbreEtat.toLocaleString()} FCFA
              </span>
            </div>
          </div>

          {/* Total */}
          <div className="pt-4 border-t-2 border-gray-300">
            <div className="flex justify-between items-center">
              <span className="text-lg font-bold text-gray-800">TOTAL À PAYER (TTC)</span>
              <span className="text-2xl font-bold text-orange-600">
                {consumption.costs.totalTTC.toLocaleString()} FCFA
              </span>
            </div>
          </div>
        </div>
      </Card>

      {/* Graphique */}
      {history.length > 0 && (
        <Card title="📈 Historique de consommation">
          <Line data={chartData} options={chartOptions} />
        </Card>
      )}
    </div>
  );
};

export default SwitchConsumption;